// src/lib/formatters/billing.ts
import {
  toPriceFrequencyLabel,
  priceToBillingType
} from "@sequencehq/core-models";
var toBillingTypeWithFrequency = ({
  billingType,
  pricingType,
  billingFrequency
}) => {
  const billingTypeLabel = priceToBillingType({
    billingType,
    pricingType
  });
  const priceFrequencyLabel = toPriceFrequencyLabel(
    pricingType,
    billingFrequency
  );
  return billingTypeLabel ? `${billingTypeLabel} / ${priceFrequencyLabel}` : priceFrequencyLabel;
};
var formatSortCode = ({ sortCode }) => [sortCode.slice(0, 2), sortCode.slice(2, 4), sortCode.slice(4)].join("-");

// src/lib/formatters/dates.ts
import { isValidDate } from "@sequencehq/validation";
import {
  add,
  endOfDay,
  formatISO,
  isEqual,
  startOfDay,
  sub,
  format,
  formatDistanceStrict
} from "@sequencehq/utils/dates";
var dateTimeWithFormat = (value, formatString) => {
  if (!value) {
    return "";
  }
  const dateObject = new Date(value);
  if (dateObject.toString() === "Invalid Date") {
    return "";
  }
  return format(dateObject, formatString);
};
var localDateWithFormat = (value, formatString) => {
  if (!value) {
    return "";
  }
  const valueToParse = value.includes("T") ? value : `${value}T00:00:00.000Z`;
  const dateObject = new Date(Date.parse(valueToParse));
  if (dateObject.toString() === "Invalid Date") {
    return "";
  }
  return format(dateObject, formatString);
};
var formatISODate = (unformattedDate) => {
  return formatISO(unformattedDate, { representation: "date" });
};
var pastRelativeToDate = (relativeToDate, duration) => formatISO(startOfDay(sub(relativeToDate, duration)));
var futureRelativeToDate = (relativeToDate, duration) => formatISO(endOfDay(add(relativeToDate, duration)));
var formatRangeDate = (date, fallback = "") => {
  return isValidDate(date) ? format(date, "yyyy-MM-dd") : fallback;
};
var relativeTimeToNow = ({
  dateTarget: dateTargetParam,
  dateNow: dateNowParam
}) => {
  const dateNow = dateNowParam ? new Date(Date.parse(dateNowParam)) : /* @__PURE__ */ new Date();
  const dateTarget = new Date(Date.parse(dateTargetParam));
  if (dateNow.toString() === "Invalid Date" || dateTarget.toString() === "Invalid Date") {
    return "";
  }
  if (isEqual(startOfDay(dateTarget), add(startOfDay(dateNow), { days: 1 }))) {
    return "tomorrow";
  }
  if (isEqual(startOfDay(dateTarget), startOfDay(dateNow))) {
    return "today";
  }
  if (isEqual(startOfDay(dateTarget), add(startOfDay(dateNow), { days: -1 }))) {
    return "yesterday";
  }
  const hasElapsed = dateNow.getTime() > dateTarget.getTime();
  const prefix = hasElapsed ? "" : "in ";
  const suffix = hasElapsed ? " ago" : "";
  return `${prefix}${formatDistanceStrict(
    endOfDay(dateTarget),
    endOfDay(dateNow)
  )}${suffix}`;
};

// src/lib/formatters/formatters.tsx
import { Box, Checkbox, Text } from "@chakra-ui/react";
import { Fragment, jsx } from "react/jsx-runtime";
var plain = (value) => {
  return typeof value === "string" ? /* @__PURE__ */ jsx(Fragment, { children: value }) : null;
};
var notInUse = () => {
  return /* @__PURE__ */ jsx(Fragment, { children: " - " });
};
var noWrap = (value = "") => /* @__PURE__ */ jsx(Text, { whiteSpace: "nowrap", children: value });
var nth = (value) => {
  if (value != null && value !== "" && !isNaN(Number(value.toString()))) {
    if (value.slice(-1) === "1" && value !== "11") {
      return `${value}st`;
    } else if (value.slice(-1) === "2" && value !== "12") {
      return `${value}nd`;
    } else if (value.slice(-1) === "3" && value !== "13") {
      return `${value}rd`;
    } else {
      return `${value}th`;
    }
  } else {
    return value;
  }
};
var conditional = ({ condition, left, right }) => (model) => {
  return condition(model) ? left(model) : right(model);
};
var iban = (value) => value.match(/.{1,4}/g)?.join(" ") ?? "";
var toPercentage = (value) => {
  return `${value}%`;
};
var previewValues = (value) => {
  return value !== void 0 ? /* @__PURE__ */ jsx(Text, { textStyle: "previewText", children: value }) : /* @__PURE__ */ jsx(
    Box,
    {
      height: "20px",
      width: "120px",
      borderRadius: "24px",
      backgroundColor: "#A9B3C5",
      opacity: "0.24"
    }
  );
};
var toTitleCase = (value) => {
  return value ? value.replace(/\w\S*/g, function(txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  }).replaceAll("_", " ") : "";
};
var isSelected = (isChecked) => {
  return /* @__PURE__ */ jsx(Checkbox, { colorScheme: "indigo.50", isChecked });
};

// src/lib/formatters/numbers.ts
var formatInteger = (prefix = "") => (value) => {
  if (typeof value !== "string" && typeof value !== "number") {
    return "";
  }
  const result = typeof value === "string" ? parseInt(value) : value;
  return isNaN(result) ? "" : `${prefix}${result.toLocaleString("en-US")}`;
};
var formatDecimal = ({ prefix = "", defaultNumber = "" } = {}) => (value) => {
  const defaultValue = `${prefix}${defaultNumber}`;
  if (typeof value !== "string") {
    return defaultValue;
  }
  const [wholeNumbers, decimals] = value.split(".");
  const parsedWholeNumbers = parseFloat(wholeNumbers);
  if (isNaN(parsedWholeNumbers)) {
    return defaultValue;
  }
  const formattedWholeNumbers = `${prefix}${parsedWholeNumbers.toLocaleString(
    "en-US"
  )}`;
  return decimals === void 0 ? formattedWholeNumbers : `${formattedWholeNumbers}.${decimals}`;
};
var formatFileSizeToHumanReadable = (bytes, decimals = 0) => {
  if (bytes <= 0) {
    return "0 Bytes";
  }
  const marker = 1e3;
  const decimalPlaces = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const count = Math.floor(Math.log(bytes) / Math.log(marker));
  return `${parseFloat(
    (bytes / Math.pow(marker, count)).toFixed(decimalPlaces)
  )} ${sizes[count]}`;
};
var formatDecimalPlaces = (value, decimalPlaces = 2) => {
  const number = parseFloat(value.toString());
  const fixed = number.toFixed(decimalPlaces);
  return parseFloat(fixed).toString();
};
export {
  conditional,
  dateTimeWithFormat,
  formatDecimal,
  formatDecimalPlaces,
  formatFileSizeToHumanReadable,
  formatISODate,
  formatInteger,
  formatRangeDate,
  formatSortCode,
  futureRelativeToDate,
  iban,
  isSelected,
  localDateWithFormat,
  noWrap,
  notInUse,
  nth,
  pastRelativeToDate,
  plain,
  previewValues,
  relativeTimeToNow,
  toBillingTypeWithFrequency,
  toPercentage,
  toTitleCase
};
//# sourceMappingURL=index.js.map